import '../../styles/tools.css';
import { PageShell } from '../../components';
import { PageBody, PageHero, Prose, Section } from '../../components/page';
import { signupFrom } from '../../components/blog/Figure';
import { Q } from '../../components/mdx';
import { SiteLink } from '../../components/SiteLink';
import type { ToolEntry } from '../../tools/registry';
import { WIDGETS } from '../../tools/widgets';

/** Turns [text](url) in registry copy into links: root-relative ones stay in the app. */
function withLinks(text: string) {
  return text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
    const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (!m) return part;
    return m[2].startsWith('/') ? (
      <SiteLink key={i} to={m[2]}>
        {m[1]}
      </SiteLink>
    ) : (
      <a key={i} href={m[2]} target="_blank" rel="noopener noreferrer">
        {m[1]}
      </a>
    );
  });
}

/**
 * One free tool. The order is fixed: title, the one-line value, the widget,
 * a CTA to Statio, then the explanation and the FAQ. The copy comes from
 * src/tools/registry.ts and the widget from src/tools/widgets.tsx.
 */
export default function ToolPage({ tool }: { tool: ToolEntry }) {
  const Widget = WIDGETS[tool.slug];
  const faq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: tool.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  return (
    <PageShell>
      <PageBody>
        {tool.faq.length > 0 && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faq) }} />
        )}
        <div className="tl-hero">
          <PageHero title={tool.h1} lede={tool.value} />
        </div>
        <section className="tl-widget" aria-label={tool.title}>
          {Widget ? <Widget /> : null}
        </section>
        <aside className="bl-cta tl-cta">
          <p>
            <b>Statio checks every agent tool call against who is allowed to make it.</b> Free for 3 MCP servers, no
            card.
          </p>
          <a
            className="s-btn s-btn-pri"
            href={signupFrom(`tool-${tool.slug}`)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Start free
          </a>
        </aside>
        {tool.about.length > 0 && (
          <Section id="about" title="How it works">
            <Prose>
              {tool.about.map((p, i) => (
                <p key={i}>{withLinks(p)}</p>
              ))}
            </Prose>
          </Section>
        )}
        {tool.faq.length > 0 && (
          <Section id="faq" title="Questions">
            {tool.faq.map((f) => (
              <Q key={f.q} q={f.q}>
                {f.a}
              </Q>
            ))}
          </Section>
        )}
      </PageBody>
    </PageShell>
  );
}
